import { useEffect, useState } from "react";

/**
 * The phone fork, as one question asked in one place.
 *
 * `isPhone` is **a real fork, not a set of breakpoints** — the rail becomes a tab bar, docks become
 * sheets, tables become rows, and Review withholds its navigation entirely — so it is a value the
 * app branches on rather than a media query surfaces style themselves against.
 *
 * It lived as `useState` plus a `resize` listener in `App.tsx` and was threaded as a prop through
 * ~20 call sites. Two reasons it is a hook now:
 *
 * - **`resize` is the wrong event.** It fires on every pixel of a window drag and again every time
 *   a mobile browser collapses or restores its toolbar, each one calling `setState` with a boolean
 *   that changed at most once in the whole gesture. `matchMedia`'s `change` fires when the answer
 *   actually changes, which is the thing being asked.
 * - **The phone pass adds branches to surfaces that do not take the prop today** — Cards, Stats,
 *   Setup, Library — and threading a value derived from `window` down four more levels is the
 *   wrong shape for it.
 *
 * It replaces the *threading*, not the contract. Where a parent genuinely decides something for a
 * child it stays a prop: `Review` is handed its tab bar rather than rendering one, because a
 * sitting withholds it and that is `Review`'s judgement to make, not the bar's.
 *
 * `lib/standalone.ts` is the pattern this follows, including the guard — `matchMedia` is absent in
 * a non-DOM test environment, and `npm test` has no DOM at all.
 */
export const PHONE_MAX_PX = 720;

const QUERY = `(max-width: ${PHONE_MAX_PX}px)`;

export function usePhone(): boolean {
  const [isPhone, setIsPhone] = useState(matches);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const list = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsPhone(event.matches);
    list.addEventListener("change", onChange);
    // The width can already have crossed the boundary between the first render and this effect —
    // a restored window, or StrictMode's second mount — so the answer is re-read rather than
    // trusted from mount.
    setIsPhone(list.matches);
    return () => list.removeEventListener("change", onChange);
  }, []);

  return isPhone;
}

function matches(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}
